<template>
    <div class="policy-wrapper p-4 md:p-6 surface-card border-round-xl shadow-1 text-left">
        <div class="mb-5">
            <p class="text-900 mb-2">Version: 1.0</p>
            <p class="text-600 italic font-light">
                These Terms take effect from the date you access, browse, register an account on, or otherwise use the Platform or its services.
            </p>
        </div>

        <div class="policy-section mb-5">
            <h2 class="H5-SemiBold text-primary mb-3">1. INTRODUCTION</h2>
            <p class="text-body mb-2">1.1 This Cookie Policy explains how ILearn’d Tutors (“ILearn’d Tutors”, “we”, “our”, or “us”) uses cookies and similar tracking technologies on its website, mobile application, and related digital services (collectively, the “Platform”).</p>
            <p class="text-body">1.2 This Policy should be read together with the <b>Privacy Policy</b> and applies to all users of the Platform, including students, parents or legal guardians, tutors, institutes, and general users.</p>
        </div>

        <div class="policy-section mb-5">
            <h2 class="H5-SemiBold text-primary mb-3">2. WHAT COOKIES AND SIMILAR TECHNOLOGIES ARE</h2>
            <p class="text-body mb-2">2.1 Cookies are small text files placed on a user’s device when visiting a website or using an application.</p>
            <p class="text-body mb-2">2.2 Cookies and similar technologies help the Platform to:</p>
            <p class="text-body ml-3 mb-1">(a) function securely and efficiently;</p>
            <p class="text-body ml-3 mb-1">(b) remember user preferences;</p>
            <p class="text-body ml-3 mb-1">(c) enhance user experience;</p>
            <p class="text-body ml-3 mb-1">(d) analyse usage and performance; and</p>
            <p class="text-body ml-3 mb-2">(e) support security and fraud prevention.</p>
            <p class="text-body">2.3 Cookies do <b>not</b> provide ILearn’d Tutors with access to a user’s device, personal files, or private information beyond what is described in this Policy.</p>
        </div>

        <div class="policy-section mb-5">
            <h2 class="H5-SemiBold text-primary mb-3">3. TYPES OF COOKIES USED</h2>
            <p class="text-body font-bold mb-1">3.1 Strictly Necessary (Essential) Cookies</p>
            <p class="text-body mb-2">3.1.1 These cookies are required for the basic operation of the Platform and cannot be disabled.</p>
            <p class="text-body mb-1">3.1.2 They enable core functions such as:</p>
            <p class="text-body ml-3 mb-1">(a) user authentication and login;</p>
            <p class="text-body ml-3 mb-1">(b) session management;</p>
            <p class="text-body ml-3 mb-1">(c) security and fraud prevention; and</p>
            <p class="text-body ml-3 mb-3">(d) navigation and accessibility.</p>

            <p class="text-body font-bold mb-1">3.2 Performance and Analytics Cookies</p>
            <p class="text-body mb-2">3.2.1 These cookies collect aggregated and anonymized information about how users interact with the Platform, including:</p>
            <p class="text-body ml-3 mb-1">(a) page views and navigation patterns;</p>
            <p class="text-body ml-3 mb-1">(b) session duration;</p>
            <p class="text-body ml-3 mb-1">(c) device, browser, and operating system type; and</p>
            <p class="text-body ml-3 mb-2">(d) error and performance metrics.</p>
            <p class="text-body mb-2">3.2.2 Tools used may include analytics services such as <b>Google Analytics</b> or equivalent platforms.</p>
            <p class="text-body mb-3">3.2.3 These cookies are used <b>only with user consent</b>, where required by law.</p>

            <p class="text-body font-bold mb-1">3.3 Functionality Cookies</p>
            <p class="text-body mb-2">3.3.1 These cookies allow the Platform to remember user preferences and choices, including:</p>
            <p class="text-body ml-3 mb-1">(a) language settings;</p>
            <p class="text-body ml-3 mb-1">(b) saved preferences; and</p>
            <p class="text-body ml-3 mb-2">(c) login or accessibility settings.</p>
            <p class="text-body mb-3">3.3.2 Disabling these cookies may affect certain personalized features of the Platform.</p>

            <p class="text-body font-bold mb-1">3.4 Advertising and Marketing Cookies</p>
            <p class="text-body mb-2">3.4.1 These cookies may be used to:</p>
            <p class="text-body ml-3 mb-1">(a) display relevant advertisements;</p>
            <p class="text-body ml-3 mb-1">(b) measure marketing campaign effectiveness; and</p>
            <p class="text-body ml-3 mb-2">(c) limit the frequency of advertisements.</p>
            <p class="text-body mb-2">3.4.2 Advertising cookies may involve third-party partners such as <b>Google Ads</b> or <b>Meta (Facebook) Ads.</b></p>
            <p class="text-body mb-3">3.4.3 These cookies are <b>never activated without explicit user consent.</b></p>

            <p class="text-body font-bold mb-1">3.5 Third-Party Cookies</p>
            <p class="text-body mb-2">3.5.1 Some cookies are placed by third-party service providers that support the Platform, including:</p>
            <p class="text-body ml-3 mb-1">(a) payment gateways;</p>
            <p class="text-body ml-3 mb-1">(b) analytics providers;</p>
            <p class="text-body ml-3 mb-1">(c) embedded content providers (e.g. video players); and</p>
            <p class="text-body ml-3 mb-2">(d) security and fraud-prevention services.</p>
            <p class="text-body">3.5.2 Third-party cookies are governed by the respective third party’s privacy and cookie policies.</p>
        </div>

        <div class="policy-section mb-5">
            <h2 class="H5-SemiBold text-primary mb-3">4. PURPOSE OF COOKIE USAGE</h2>
            <p class="text-body mb-2">4.1 ILearn’d Tutors uses cookies to:</p>
            <p class="text-body ml-3 mb-1">(a) ensure Platform functionality and security;</p>
            <p class="text-body ml-3 mb-1">(b) improve user experience and performance;</p>
            <p class="text-body ml-3 mb-1">(c) analyze usage patterns and improve services;</p>
            <p class="text-body ml-3 mb-1">(d) personalize content where permitted; and</p>
            <p class="text-body ml-3 mb-2">(e) support lawful marketing activities.</p>
            <p class="text-body">4.2 ILearn’d Tutors <b>does not sell personal data</b> collected through cookies.</p>
        </div>

        <div class="policy-section mb-5">
            <h2 class="H5-SemiBold text-primary mb-3">5. USER CONSENT AND COOKIE MANAGEMENT</h2>
            <p class="text-body mb-2">5.1 Where required by law, non-essential cookies shall be used <b>only after explicit user consent</b> is obtained through a cookie banner or preference center.</p>
            <p class="text-body mb-2">5.2 Users may:</p>
            <p class="text-body ml-3 mb-1">(a) accept or reject non-essential cookies;</p>
            <p class="text-body ml-3 mb-1">(b) modify cookie preferences at any time; or</p>
            <p class="text-body ml-3 mb-2">(c) withdraw consent without affecting access to essential Platform features.</p>
            <p class="text-body">5.3 For users who are minors, consent for non-essential cookies must be provided by a <b>parent or legal guardian</b>, where applicable.</p>
        </div>

        <div class="policy-section mb-5">
            <h2 class="H5-SemiBold text-primary mb-3">6. MANAGING OR DISABLING COOKIES</h2>
            <p class="text-body mb-2">6.1 Users may manage or disable cookies through their browser or device settings, including:</p>
            <p class="text-body ml-3 mb-1">(a) blocking all cookies;</p>
            <p class="text-body ml-3 mb-1">(b) blocking third-party cookies;</p>
            <p class="text-body ml-3 mb-1">(c) deleting stored cookies; or</p>
            <p class="text-body ml-3 mb-2">(d) configuring site-specific permissions.</p>
            <p class="text-body">6.2 Disabling <b>essential cookies</b> may prevent access to certain Platform features, including login and secure areas.</p>
        </div>

        <div class="policy-section mb-5">
            <h2 class="H5-SemiBold text-primary mb-3">7. COOKIES AND MOBILE APPLICATIONS</h2>
            <p class="text-body mb-2">7.1 The mobile application may use technologies similar to cookies, including:</p>
            <p class="text-body ml-3 mb-1">(a) device identifiers;</p>
            <p class="text-body ml-3 mb-1">(b) application analytics tools;</p>
            <p class="text-body ml-3 mb-1">(c) crash and error reporting tools; and</p>
            <p class="text-body ml-3 mb-2">(d) push notification identifiers.</p>
            <p class="text-body">7.2 These technologies are used solely for performance monitoring, security, and service improvement.</p>
        </div>

        <div class="policy-section mb-5">
            <h2 class="H5-SemiBold text-primary mb-3">8. DATA RETENTION AND SECURITY</h2>
            <p class="text-body mb-2">8.1 Cookie-related data shall be retained only for as long as necessary to fulfil the purposes set out in this Policy, unless a longer retention period is required by law.</p>
            <p class="text-body">8.2 ILearn’d Tutors implements reasonable technical and organizational measures to protect cookie-related data from unauthorized access or misuse.</p>
        </div>

        <div class="policy-section mb-5">
            <h2 class="H5-SemiBold text-primary mb-3">9. DATA PROTECTION AND LEGAL COMPLIANCE</h2>
            <p class="text-body mb-2">9.1 All data collected through cookies is processed in accordance with:</p>
            <p class="text-body ml-3 mb-1">(a) the <b>Personal Data Protection Act, No. 9 of 2022 of Sri Lanka (PDPA);</b> and</p>
            <p class="text-body ml-3 mb-2">(b) the Platform’s <b>Privacy Policy.</b></p>
            <p class="text-body">9.2 Users may exercise their data subject rights as set out in the Privacy Policy.</p>
        </div>

        <div class="policy-section mb-5">
            <h2 class="H5-SemiBold text-primary mb-3">10. CHANGES TO THIS COOKIE POLICY</h2>
            <p class="text-body mb-2">10.1 This Cookie Policy may be updated from time to time to reflect legal, technical, or operational changes.</p>
            <p class="text-body">10.2 Material changes shall be notified through the Platform and, where required by law, may require <b>renewed user consent.</b></p>
        </div>

        <div class="policy-section mb-5">
            <h2 class="H5-SemiBold text-primary mb-3">11. GOVERNING LAW</h2>
            <p class="text-body">11.1 This Cookie Policy shall be governed by and construed in accordance with the laws of the <b>Democratic Socialist Republic of Sri Lanka.</b></p>
        </div>

        <div class="policy-section">
            <h2 class="H5-SemiBold text-primary mb-3">16. CONTACT INFORMATION</h2>
            <div class="p-3 border-round bg-faded">
                <p class="text-body font-bold mb-1"><b>ILearn’d Tutors – Legal Team</b></p>
                <p class="text-body mb-1">Email: support@ilearndsl.com</p>
                <p class="text-body mb-0">Address: 306/1 Highlevel Road, Colombo 06, Sri Lanka</p>
            </div>
        </div>
    </div>
</template>

<style scoped>
.text-primary { color: #1D97BD !important; }
.bg-faded { background-color: rgba(29, 151, 189, 0.04); }
.text-body {
    font-size: 0.95rem;
    color: #4A5568;
    font-weight: 400;
    line-height: 1.6;
}
.ml-3 { margin-left: 1.5rem; }
</style>